/* The preview, measurements and cart all use the same physical tiles. */
(function (scope) {
  "use strict";
  const dimensions = {
    "large-parallel-tile": [240, 240, "EngravedLargeParallel"],
    "parallel-tile": [120, 120, "EngravedParallelVertical"],
    "zen-tile": [240, 240, "EngravedZen"],
    "subway-tile": [240, 240, "EngravedSubway"],
    "2x2-tile": [240, 240, "LargeSquare"],
    "large-square-tile": [240, 240, "LargeSquare"],
    "large-halflap-tile": [120, 240, "LargeHalflapVertical"],
    "halflap-tile": [60, 120, "HalflapVertical"],
    "shiplap-tile": [240, 60, "ShiplapHorizontal"],
    "mini-shiplap-tile": [120, 30, "MiniShiplapHorizontal"],
    "quarter-tile": [60, 60, "Quarter"],
    "square-tile": [120, 120, "Square"],
    "half-circle-tile": [120, 60, "HalfCircleHorizontal"],
    "small-half-circle-tile": [60, 30, "SmallHalfCircleHorizontal"],
    "9mm-pinstripe-printed-2x2-tile": [240, 240, "Pinstripe"],
    "9mm-aspen-printed-2x2-tile": [240, 240, "Aspen"],
    "9mm-bamboo-printed-2x2": [240, 240, "Bamboo"],
    "9mm-geo-printed-2x2": [240, 240, "Geo"],
    "9mm-strand-printed-2x2-tile": [240, 240, "Strand90"],
  };
  const ARC_HANDLES = ["half-circle-tile", "small-half-circle-tile"];
  function box(shape) {
    let w = Number(shape.width),
      h = Number(shape.height);
    let ox = Number(shape.offsetX ?? w / 2),
      oy = Number(shape.offsetY ?? h / 2);
    if (shape.type === "arc") {
      // Half circles come in two sizes; fall back to the full-size tile.
      w = w > 0 ? w : 120;
      h = h > 0 ? h : w / 2;
      ox = w / 2;
      oy = 0;
    }
    if (!(w > 0 && h > 0))
      throw new Error("This design has an unsupported tile size.");
    const angle = ((Number(shape.rotation) || 0) * Math.PI) / 180;
    const corners = [
      [-ox, -oy],
      [w - ox, -oy],
      [-ox, h - oy],
      [w - ox, h - oy],
    ].map(([x, y]) => ({
      x: shape.x + x * Math.cos(angle) - y * Math.sin(angle),
      y: shape.y + x * Math.sin(angle) + y * Math.cos(angle),
    }));
    const x = Math.min(...corners.map((p) => p.x)),
      y = Math.min(...corners.map((p) => p.y));
    return {
      x,
      y,
      width: Math.max(...corners.map((p) => p.x)) - x,
      height: Math.max(...corners.map((p) => p.y)) - y,
    };
  }
  function bounds(shapes) {
    const boxes = shapes.map(box);
    const x = Math.min(...boxes.map((b) => b.x)),
      y = Math.min(...boxes.map((b) => b.y));
    return {
      x,
      y,
      width: Math.max(...boxes.map((b) => b.x + b.width)) - x,
      height: Math.max(...boxes.map((b) => b.y + b.height)) - y,
    };
  }
  function fromLayout(entries, variations, studioShapes = [], { preserveSavedColors = false } = {}) {
    const expected = entries.flatMap((entry) => (entry.configurations || []).map((config) => ({
      handle: entry.handle || variations.find((v) => String(v.variant_id) === String(entry.variant_id))?.handle,
      config,
    })));
    const used = new Set();
    const matching = expected.map(({ handle, config }) => {
      const index = studioShapes.findIndex((shape, i) => !used.has(i) &&
        (shape.metadata?.handle || variations.find((v) => String(v.variant_id) === String(shape.metadata?.variant_id))?.handle) === handle &&
        Math.abs(shape.x - Number(config.tile_x_position)) < 0.01 &&
        Math.abs(shape.y - Number(config.tile_y_position)) < 0.01 &&
        Number(shape.rotation || 0) === Number(config.tile_rotation || 0));
      if (index >= 0) used.add(index);
      return studioShapes[index];
    });
    // Shared or outdated IDs must never replace a different published pattern.
    const useStudioColors = matching.length === studioShapes.length && matching.every(Boolean);
    if (preserveSavedColors && !useStudioColors)
      throw new Error("This colorway needs an updated layout matching its Studio design.");
    let tileIndex = 0;
    const shapes = [];
    entries.forEach((entry) => {
      const variation = variations.find(
        (v) => String(v.variant_id) === String(entry.variant_id),
      );
      const handle = entry.handle || variation?.handle;
      const size = dimensions[handle];
      if (!size || !variation)
        throw new Error("This design needs updated tile information.");
      if (
        !entry.configurations?.length ||
        entry.configurations.length !== Number(entry.count)
      )
        throw new Error("This design needs an updated layout.");
      entry.configurations.forEach((config) => {
        const arc = ARC_HANDLES.includes(handle);
        const current = useStudioColors ? matching[tileIndex] : null;
        tileIndex++;
        const original = current || studioShapes.find(
          (s) =>
            String(s.metadata?.variant_id) === String(entry.variant_id) &&
            Math.abs(s.x - Number(config.tile_x_position)) < 0.01 &&
            Math.abs(s.y - Number(config.tile_y_position)) < 0.01 &&
            Number(s.rotation || 0) === Number(config.tile_rotation || 0),
        );
        const color = current && !preserveSavedColors
          ? variations.find((v) => String(v.variant_id) === String(current.metadata?.variant_id))
          : variation;
        if (!color) throw new Error("This design needs updated tile information.");
        shapes.push({
          id: "tile-" + shapes.length,
          type: arc ? "arc" : "rect",
          x: Number(config.tile_x_position),
          y: Number(config.tile_y_position),
          width: size[0],
          height: size[1],
          offsetX: size[0] / 2,
          offsetY: arc ? 0 : size[1] / 2,
          rotation: Number(config.tile_rotation) || 0,
          ...(original || {}),
          offsetX: original?.offsetX ?? (original?.width || size[0]) / 2,
          offsetY: arc
            ? 0
            : (original?.offsetY ?? (original?.height || size[1]) / 2),
          metadata: {
            shape_id: original?.metadata?.shape_id || size[2],
            shape_label: original?.metadata?.shape_label || size[2],
            handle,
            product_id: String(entry.product_id),
            variant_id: String(color.variant_id),
            photo_url: color.photo,
            price: Number(color.price),
          },
        });
      });
    });
    return shapes;
  }
  function fromStudio(design, variations) {
    if (!Array.isArray(design?.shapes) || !design.shapes.length)
      throw new Error("This design is not ready yet. Please choose another design.");
    return design.shapes.map((shape) => {
      const variation = variations.find((v) =>
        String(v.variant_id) === String(shape.metadata?.variant_id),
      );
      const handle = variation?.handle || shape.metadata?.handle;
      const size = dimensions[handle];
      const wood = handle === "acoustic-wood-slat-wainscoting-panel";
      if (!variation || (!size && !wood) ||
          shape.type !== (ARC_HANDLES.includes(handle) ? "arc" : "rect"))
        throw new Error("This design needs updated tile information.");
      const width = Number(shape.width ?? size?.[0]);
      const height = Number(shape.height ?? size?.[1]);
      const x = Number(shape.x), y = Number(shape.y);
      const rotation = Number(shape.rotation ?? 0);
      const offsetX = Number(shape.offsetX ?? width / 2);
      const offsetY = Number(shape.offsetY ?? (shape.type === "arc" ? 0 : height / 2));
      if (![width, height, x, y, rotation, offsetX, offsetY].every(Number.isFinite) ||
          width <= 0 || height <= 0)
        throw new Error("This design needs an updated layout.");
      return {
        ...shape, x, y, width, height, rotation, offsetX, offsetY,
        metadata: {
          ...shape.metadata,
          handle,
          product_id: String(variation.product_id),
          variant_id: String(variation.variant_id),
          photo_url: variation.photo,
          price: Number(variation.price),
        },
      };
    });
  }
  function repeatColumns(columns) {
    let offset = 20;
    const output = [];
    columns.forEach((column, c) => {
      const b = bounds(column);
      column.forEach((s, t) => {
        const clone = JSON.parse(JSON.stringify(s));
        clone.id = "column-" + c + "-tile-" + t;
        clone.x = s.x - b.x + offset;
        clone.y = s.y - b.y + 20;
        clone.attrs = { id: clone.id, rotation: clone.rotation };
        output.push(clone);
      });
      offset += b.width;
    });
    return output;
  }
  function hasWoodSlats(shapes) {
    return shapes.some((shape) =>
      shape.metadata?.handle === "acoustic-wood-slat-wainscoting-panel",
    );
  }
  function quantities(columns, requestedInches, manualSets) {
    // Wainscoting wood columns are sold as one two-panel pack per column.
    // Their untrimmed physical width must not inflate nominal wall coverage.
    const width = Math.round(columns.reduce((sum, col) =>
      sum + (hasWoodSlats(col) ? 240 : bounds(col).width), 0) * 1e5) / 1e6;
    if (!(width > 0) || !Number.isFinite(width)) throw new Error("Choose a valid set first.");
    const sets =
      manualSets == null
        ? Math.max(1, Math.ceil(Math.max(0, requestedInches) / width))
        : Math.max(1, Math.floor(manualSets));
    return {
      width,
      sets,
      totalColumns: columns.length * sets,
      coverage: width * sets,
    };
  }
  function cartTiles(shapes) {
    const tiles = {};
    shapes.forEach((shape) => {
      const m = shape.metadata;
      if (!m.variant_id || !Number.isFinite(m.price) || m.price <= 0)
        throw new Error(
          "A tile has no valid price. Please choose another color.",
        );
      const row = (tiles[m.variant_id] ||= {
        variant_id: m.variant_id,
        quantity: 0,
        cost: 0,
      });
      row.quantity++;
      row.cost = Math.round((row.cost + m.price) * 100) / 100;
    });
    return tiles;
  }
  // tileWidth/tileHeight are the half circle's size (120x60 or 60x30).
  function arcTexture(width, height, tileWidth = 120, tileHeight = tileWidth / 2) {
    const w = tileWidth, h = tileHeight;
    return width < height
      ? { x: 0, y: -w / 2, width: h, height: w, rotation: 90 }
      : { x: -w / 2, y: -h, width: w, height: h, rotation: 180 };
  }
  function rotateTexture(shape, width, height) {
    return shape.type !== "arc" && width > 0 && height > 0 &&
      ((shape.width > shape.height && width < height) ||
       (shape.width < shape.height && width > height));
  }
  const api = { arcTexture, rotateTexture, box, bounds, fromLayout, fromStudio, repeatColumns, quantities, cartTiles, hasWoodSlats };
  if (typeof module !== "undefined") module.exports = api;
  else scope.WainscotingCore = api;
})(typeof window === "undefined" ? globalThis : window);